import { useState, useEffect, useRef, useCallback } from 'react';
import { fetchPatients, fetchPatientOutstandingClaims, recordSelfPayPayment } from '../services/api';
import './SelfPayModal.css';

const PAYMENT_METHODS = [
  { value: 'cash',         label: 'Cash' },
  { value: 'check',        label: 'Check' },
  { value: 'card',         label: 'Credit Card' },
  { value: 'money_order',  label: 'Money Order' },
  // { value: 'online',       label: 'Online / Portal' },
];

const REF_LABELS = {
  check:       'Check Number',
  card:        'Auth / Approval Code',
  online:      'Transaction ID',
  money_order: 'Money Order #',
  cash:        'Receipt #',
};

function fmt(n) {
  return '$' + (parseFloat(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

export default function SelfPayModal({ onClose, onSaved }) {
  const [step, setStep] = useState('patient'); // 'patient' | 'claim' | 'payment'

  // Patient search
  const [patientSearch, setPatientSearch]   = useState('');
  const [patients, setPatients]             = useState([]);
  const [patientLoading, setPatientLoading] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const searchTimer = useRef(null);

  // Claim selection
  const [claims, setClaims]             = useState([]);
  const [claimsLoading, setClaimsLoading] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState(null);

  // Payment form
  const [form, setForm] = useState({
    payment_date:    today(),
    amount:          '',
    payment_method:  'check',
    reference_number: '',
    notes:           '',
  });
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState(null);
  const [success, setSuccess] = useState(false);

  const searchPatients = useCallback(async (q) => {
    if (!q.trim()) { setPatients([]); return; }
    setPatientLoading(true);
    try {
      const res = await fetchPatients({ q: q.trim() });
      setPatients(res.data?.patients ?? res.data ?? []);
    } catch { setPatients([]); }
    finally  { setPatientLoading(false); }
  }, []);

  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => searchPatients(patientSearch), 280);
    return () => clearTimeout(searchTimer.current);
  }, [patientSearch, searchPatients]);

  const selectPatient = async (p) => {
    setSelectedPatient(p);
    setStep('claim');
    setClaimsLoading(true);
    try {
      const res = await fetchPatientOutstandingClaims(p.patient_id ?? p.id);
      setClaims(res.data ?? []);
    } catch { setClaims([]); }
    finally  { setClaimsLoading(false); }
  };

  const selectClaim = (claim) => {
    setSelectedClaim(claim);
    setForm((f) => ({ ...f, amount: claim.outstanding > 0 ? claim.outstanding.toFixed(2) : '' }));
    setStep('payment');
    setError(null);
  };

  // Soft, overridable overpayment guard: warn (don't block) when the entered amount
  // exceeds the claim's remaining patient balance. Overpayments are legitimate (e.g.
  // insurance later lowers patient responsibility), so the excess is posted as a
  // refundable patient credit rather than being rejected at entry.
  const overpayment = selectedClaim
    ? Math.max(0, (parseFloat(form.amount) || 0) - (selectedClaim.outstanding || 0))
    : 0;

  const handleSubmit = async () => {
    setError(null);
    if (!form.amount || parseFloat(form.amount) <= 0) {
      setError('Please enter a valid payment amount.');
      return;
    }
    setSaving(true);
    try {
      await recordSelfPayPayment(selectedPatient.patient_id ?? selectedPatient.id, {
        claim_id:         selectedClaim.claim_id,
        payment_date:     form.payment_date,
        amount:           parseFloat(form.amount),
        payment_method:   form.payment_method,
        reference_number: form.reference_number || null,
        notes:            form.notes || null,
      });
      setSuccess(true);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to record payment.');
    } finally {
      setSaving(false);
    }
  };

  const handleDone = () => {
    onSaved?.();
    onClose();
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="spm-overlay">
      <div className="spm-panel">

        {/* Header */}
        <div className="spm-header">
          <div>
            <h2 className="spm-title">Record Self-Pay Payment</h2>
            <p className="spm-sub">
              {step === 'patient' && 'Search for a patient'}
              {step === 'claim'   && selectedPatient && `${selectedPatient.first_name} ${selectedPatient.last_name}${selectedPatient.facility_name ? ` · ${selectedPatient.facility_name}` : ''} — select a claim`}
              {step === 'payment' && selectedClaim   && `Claim ${selectedClaim.claim_number} — ${fmtDate(selectedClaim.service_date)}`}
            </p>
          </div>
          <button className="spm-close" onClick={onClose} aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Step indicator */}
        <div className="spm-steps">
          {['Patient', 'Claim', 'Payment'].map((label, idx) => {
            const stepKey = ['patient', 'claim', 'payment'][idx];
            const active  = step === stepKey;
            const done    = (idx === 0 && step !== 'patient') || (idx === 1 && step === 'payment');
            return (
              <div key={label} className={`spm-step ${active ? 'active' : ''} ${done ? 'done' : ''}`}>
                <div className="spm-step-dot">
                  {done
                    ? <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                    : idx + 1
                  }
                </div>
                <span className="spm-step-label">{label}</span>
                {idx < 2 && <div className="spm-step-line" />}
              </div>
            );
          })}
        </div>

        {/* Body */}
        <div className="spm-body">

          {/* ── Step 1: Patient Search ── */}
          {step === 'patient' && (
            <div className="spm-patient-search">
              <label className="spm-label">Patient Name or MRN</label>
              <div className="spm-search-wrap">
                <svg className="spm-search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                  className="spm-search-input"
                  type="text"
                  placeholder="Search patients…"
                  value={patientSearch}
                  onChange={(e) => setPatientSearch(e.target.value)}
                  autoFocus
                />
                {patientLoading && <div className="spm-search-spinner" />}
              </div>

              {patientSearch.trim().length > 0 && (
                <div className="spm-patient-list">
                  {patients.length === 0 && !patientLoading && (
                    <div className="spm-patient-empty">No patients found</div>
                  )}
                  {patients.map((p) => (
                    <button key={p.patient_id ?? p.id} className="spm-patient-row" onClick={() => selectPatient(p)}>
                      <div className="spm-patient-name-row">
                        <span className="spm-patient-name">{p.first_name} {p.last_name}</span>
                        {p.facility_name && <span className="spm-patient-facility">{p.facility_name}</span>}
                      </div>
                      <div className="spm-patient-meta">
                        DOB: {fmtDate(p.date_of_birth)}
                        {p.emr_patient_id && <> &bull; MRN: {p.emr_patient_id}</>}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Step 2: Claim Selection ── */}
          {step === 'claim' && (
            <div className="spm-claims">
              {claimsLoading ? (
                <div className="spm-loading"><div className="spm-spinner" /><span>Loading claims…</span></div>
              ) : claims.length === 0 ? (
                <div className="spm-empty">No outstanding balances found for this patient.</div>
              ) : (
                <div className="spm-claims-table-wrap">
                  <table className="spm-claims-table">
                    <thead>
                      <tr>
                        <th>Claim #</th>
                        <th>Svc Date</th>
                        <th>Provider</th>
                        <th className="r">Charges</th>
                        <th className="r">Ins. Paid</th>
                        <th className="r">Pt. Resp.</th>
                        <th className="r">Already Paid</th>
                        <th className="r">Outstanding</th>
                      </tr>
                    </thead>
                    <tbody>
                      {claims.map((c) => (
                        <tr
                          key={c.claim_id}
                          className="spm-claim-row"
                          onClick={() => selectClaim(c)}
                        >
                          <td className="spm-claim-num">{c.claim_number}</td>
                          <td>{fmtDate(c.service_date)}</td>
                          <td>{c.rendering_provider || '—'}</td>
                          <td className="r">{fmt(c.total_charge)}</td>
                          <td className="r">{fmt(c.insurance_paid)}</td>
                          <td className="r">{fmt(c.patient_responsibility)}</td>
                          <td className="r">{fmt(c.patient_paid)}</td>
                          <td className="r spm-outstanding">
                            <strong>{fmt(c.outstanding)}</strong>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="spm-back-row">
                <button className="spm-btn-back" onClick={() => setStep('patient')}>← Back</button>
              </div>
            </div>
          )}

          {/* ── Step 3: Payment Entry ── */}
          {step === 'payment' && !success && (
            <div className="spm-payment-form">
              <div className="spm-claim-summary">
                <div className="spm-cs-row">
                  <span className="spm-cs-label">Claim</span>
                  <span className="spm-cs-val">{selectedClaim.claim_number}</span>
                </div>
                <div className="spm-cs-row">
                  <span className="spm-cs-label">Service Date</span>
                  <span className="spm-cs-val">{fmtDate(selectedClaim.service_date)}</span>
                </div>
                <div className="spm-cs-row">
                  <span className="spm-cs-label">Outstanding Balance</span>
                  <span className="spm-cs-val spm-cs-outstanding">{fmt(selectedClaim.outstanding)}</span>
                </div>
              </div>

              <div className="spm-form-grid">
                <div className="spm-field">
                  <label className="spm-label">Payment Date</label>
                  <input
                    className="spm-input"
                    type="date"
                    value={form.payment_date}
                    onChange={(e) => setForm((f) => ({ ...f, payment_date: e.target.value }))}
                  />
                </div>
                <div className="spm-field">
                  <label className="spm-label">Amount</label>
                  <div className="spm-money-wrap">
                    <span className="spm-money-symbol">$</span>
                    <input
                      className="spm-input spm-money-input"
                      type="number"
                      min="0.01"
                      step="0.01"
                      placeholder="0.00"
                      value={form.amount}
                      onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                    />
                  </div>
                </div>
              </div>

              <div className="spm-field spm-field-full">
                <label className="spm-label">Payment Method</label>
                <div className="spm-method-pills">
                  {PAYMENT_METHODS.map((m) => (
                    <button
                      key={m.value}
                      className={`spm-method-pill ${form.payment_method === m.value ? 'active' : ''}`}
                      onClick={() => setForm((f) => ({ ...f, payment_method: m.value }))}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="spm-field spm-field-full">
                <label className="spm-label">{REF_LABELS[form.payment_method] || 'Reference Number'}</label>
                <input
                  className="spm-input"
                  type="text"
                  placeholder="Optional"
                  value={form.reference_number}
                  onChange={(e) => setForm((f) => ({ ...f, reference_number: e.target.value }))}
                />
              </div>

              <div className="spm-field spm-field-full">
                <label className="spm-label">Notes</label>
                <textarea
                  className="spm-textarea"
                  rows="2"
                  placeholder="Optional"
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                />
              </div>

              {overpayment > 0 && (
                <div className="spm-warning" role="alert">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                    <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                  <span>
                    This exceeds the outstanding balance of {fmt(selectedClaim.outstanding)} by{' '}
                    <strong>{fmt(overpayment)}</strong>. The excess will be recorded as a patient credit
                    (refundable). You can still post it.
                  </span>
                </div>
              )}

              {error && <div className="spm-error">{error}</div>}

              <div className="spm-back-row">
                <button className="spm-btn-back" onClick={() => setStep('claim')} disabled={saving}>← Back</button>
              </div>
            </div>
          )}

          {/* ── Success ── */}
          {success && (
            <div className="spm-success">
              <div className="spm-success-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" /><polyline points="9 12 11 14 15 10" />
                </svg>
              </div>
              <h3 className="spm-success-title">Payment Recorded</h3>
              <p className="spm-success-msg">
                {fmt(form.amount)} posted to {selectedPatient.first_name} {selectedPatient.last_name} — Claim {selectedClaim.claim_number}
              </p>
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="spm-footer">
          {success ? (
            <>
              <button className="spm-btn-secondary" onClick={() => {
                setStep('patient');
                setPatientSearch('');
                setPatients([]);
                setSelectedPatient(null);
                setSelectedClaim(null);
                setForm({ payment_date: today(), amount: '', payment_method: 'check', reference_number: '', notes: '' });
                setSuccess(false);
                setError(null);
              }}>
                Record Another
              </button>
              <button className="spm-btn-primary" onClick={handleDone}>Done</button>
            </>
          ) : step === 'payment' ? (
            <>
              <button className="spm-btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
              <button className="spm-btn-primary" onClick={handleSubmit} disabled={saving}>
                {saving ? 'Posting…' : 'Post Payment'}
              </button>
            </>
          ) : (
            <button className="spm-btn-secondary" onClick={onClose}>Cancel</button>
          )}
        </div>

      </div>
    </div>
  );
}
